import React from "react";
import { ApiError, cockpit, getTenantContext, type MessageView } from "@/api";
import { Banner, Button, Dialog } from "@/ds";
import { Icon, fmtStamp } from "@/lib";
import { useT } from "../../i18n";

const REFUSALS = ["not_in_trash", "version_mismatch", "origin_message", "last_message", "dependent_history", "delivery_pending", "purge_due", "shared_content"] as const;

/** The confirmation freezes both the selected message and the observed request version. */
export function DeleteMessageAction({ requestId, version, message, onChanged }: {
  requestId: string;
  version: number | undefined;
  message: MessageView;
  onChanged: (status: "deleted" | "pending") => void;
}) {
  const t = useT();
  const [confirmation, setConfirmation] = React.useState<{ version: number; tenant: string | null } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [uncertain, setUncertain] = React.useState(false);
  const live = React.useRef(true);
  React.useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  if (!message.id || version === undefined) return null;
  const remove = async () => {
    if (!confirmation || busy || uncertain || !message.id) return;
    if (getTenantContext() !== confirmation.tenant) { setError(t("ws.messageDelete.stale")); return; }
    setBusy(true);
    setError(null);
    try {
      const result = await cockpit.deleteMessage(requestId, message.id, confirmation.version);
      if (!live.current || getTenantContext() !== confirmation.tenant) return;
      setConfirmation(null);
      onChanged(result.status);
    } catch (cause) {
      if (!live.current || getTenantContext() !== confirmation.tenant) return;
      const data = cause instanceof ApiError ? cause.data as { code?: string; reason?: string } | null : null;
      const reason = data?.code === "MESSAGE_DELETION_REFUSED" ? REFUSALS.find((r) => r === data.reason) : undefined;
      if (reason) setError(t(`ws.messageDelete.refused.${reason}`));
      else if (cause instanceof ApiError && cause.status === 403) setError(t("ws.messageDelete.forbidden"));
      else if (cause instanceof ApiError && cause.status === 404) setError(t("ws.messageDelete.stale"));
      else { setUncertain(true); setError(t("ws.messageDelete.unknown")); }
    } finally { if (live.current) setBusy(false); }
  };
  return <>
    <Button variant="ghost" size="sm" iconLeft={<Icon name="trash" size={15} />}
      onClick={() => { setError(null); setUncertain(false); setConfirmation({ version, tenant: getTenantContext() }); }}>
      {t("ws.messageDelete.action")}
    </Button>
    <Dialog open={confirmation !== null} onClose={busy ? undefined : () => setConfirmation(null)}
      tone="danger" icon={<Icon name="trash" size={20} />} title={t("ws.messageDelete.title")}
      footer={<>
        <Button variant="ghost" disabled={busy} onClick={() => setConfirmation(null)}>{t("common.cancel")}</Button>
        <Button variant="destructive" loading={busy} disabled={busy || uncertain} onClick={() => { void remove(); }}>
          {t("ws.messageDelete.confirm")}
        </Button>
      </>}>
      <div className="stack-sm">
        <div><strong>{message.subject || t("common.noSubject")}</strong><br />
          {message.fromAddr || t("common.noInfo")} · {fmtStamp(message.receivedAt)}</div>
        <p style={{ margin: 0 }}>{t("ws.messageDelete.body")}</p>
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
      </div>
    </Dialog>
  </>;
}
